import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { CurrentUser, Organisation, Role, UserInfo, UserRoleType } from '@digihaven/shared';

import { Logout } from '@actions/authentication.actions';
import { ErrorMessages } from '@common/entities/error/error-messages.entity';
import { Functionalities } from '@common/entities/functionalities.entity';

import { ApplicationState } from 'state';

import { HttpService } from '../http/http.service';

@Injectable()
export class AuthorizationService {
    private readonly CURRENT_USER_ENDPOINT: string = 'users/current';

    private userInfo$!: Observable<CurrentUser>;
    private accessToken$!: Observable<string>;

    constructor(private store: Store<ApplicationState>, private httpService: HttpService) {
        this.buildSourceStream();
    }

    getFunctionalities(): Observable<Functionalities> {
        return this.userInfo$.pipe(map(rawObject => this.extractFunctionalities(rawObject)));
    }

    getUserInfo(): Observable<UserInfo> {
        return this.userInfo$.pipe(map(this.extractUserInfo.bind(this)));
    }

    getCurrentUser(): Observable<CurrentUser> {
        return this.userInfo$;
    }

    getOrganisation(): Observable<Organisation> {
        return this.userInfo$.pipe(map(this.extractOrganisation.bind(this)));
    }

    private fetchUserInfo(accessToken: string | undefined): Observable<CurrentUser> {
        if (accessToken) {
            return this.httpService.get<CurrentUser>(this.CURRENT_USER_ENDPOINT);
        } else {
            this.logout();

            return throwError('NO ACCESS TOKEN');
        }
    }

    private buildSourceStream(): void {
        this.accessToken$ = this.store.select(state => state.token.userTokens.access_token);
        this.userInfo$ = this.accessToken$.pipe(switchMap(this.fetchUserInfo.bind(this)), shareReplay(1));
    }

    private extractUserInfo(currentUser: CurrentUser): UserInfo {
        return {
            username: currentUser.Login,
            roleLabel: currentUser.Roles[0].Label as UserRoleType,
            firstName: currentUser.FirstName,
            lastName: currentUser.LastName
        };
    }

    private extractOrganisation(currentUser: CurrentUser): Organisation {
        const {
            Id,
            ExternalId,
            Name,
            LongName,
            CustomProperties: { ApplicationScope, Modules }
        } = currentUser.Organisation;

        return {
            Id: Id.toString(),
            ExternalId,
            Name,
            LongName,
            ApplicationScope,
            Modules
        };
    }

    private extractFunctionalities(currentUser: CurrentUser): Functionalities {
        const roles: Role[] = currentUser.Roles;
        if (roles.length === 0) {
            throw Error(ErrorMessages.NO_FUNCTIONALITIES);
        }

        const functions: string[] = roles[0].Functions.map(val => val.toLowerCase());
        const fns: Functionalities = {
            acceptDossier: functions.includes('accept_dossier'),
            adminFunctions: functions.includes('admin_functions'),
            createDossier: functions.includes('create_dossier'),
            createReport: functions.includes('create_report'),
            destroyAndPreserveDossier: functions.includes('destroy_and_preserve_dossier'),
            downloadDossier: functions.includes('download_dossier'),
            editDossier: functions.includes('edit_dossier'),
            exportDossier: functions.includes('export_dossier'),
            generalManagement: functions.includes('general_management'),
            interruptDossier: functions.includes('interrupt_dossier'),
            manageGroupsDossier: functions.includes('manage_groups_dossier'),
            viewDossier: functions.includes('view_dossier'),
            viewUnpublishedDossier: functions.includes('view_unpublished_dossier'),
            editRelations: functions.includes('edit_relations'),
            viewReports: functions.includes('view_reports'),
            viewOrphanedDocument: functions.includes('view_orphaned_document'),
            viewEvents: functions.includes('view_events'),
            editAdministrativeDataInArchive: functions.includes('edit_administrative_data_in_archive'),
            adminOrganisation: functions.includes('admin_organisation'),
            readOrganisation: functions.includes('read_organisation')
        };

        return fns;
    }

    private logout(): void {
        this.store.dispatch(new Logout());
    }
}
